import React, { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays a fallback UI
 * Particularly useful for translation-dependent components that may fail during hydration
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    
    this.setState({ errorInfo });
    
    // Optional: Send to error tracking service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error } = this.state;
    const { 
      children, 
      fallback, 
      fallbackComponent: FallbackComponent,
      section = 'section',
      showDetails = false 
    } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // If a custom fallback component is provided, use it
      if (FallbackComponent) {
        return (
          <FallbackComponent 
            error={error} 
            onRetry={this.handleRetry}
            onRefresh={this.handleRefresh}
          />
        );
      }

      // Default fallback UI
      return (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-8 mx-4 my-8">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>
            
            <h3 className="text-xl font-semibold text-white mb-2">
              Unable to load this {section}
            </h3>
            
            <p className="text-gray-400 mb-6 max-w-md">
              Something went wrong while rendering this content. 
              This might be a temporary issue.
            </p>

            {showDetails && error && (
              <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4 mb-6 text-left w-full max-w-lg">
                <p className="text-red-400 text-sm font-mono break-all">
                  {error.toString()}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-5 py-2.5 rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              
              <button
                onClick={this.handleRefresh}
                className="flex items-center gap-2 border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Inline Error Boundary - For smaller sections that shouldn't break the whole page
 * Shows a minimal error state without overwhelming the UI
 */
export class InlineErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('InlineErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Minimal inline fallback
      return (
        <div className="text-gray-500 text-sm py-2">
          {this.props.fallbackText || 'Content unavailable'}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Translation Section Wrapper - Specifically for translation-heavy components
 * Provides a clean fallback when translations fail to load
 */
export const TranslationErrorBoundary = ({ children, sectionName }) => {
  return (
    <ErrorBoundary 
      section={sectionName || 'content'}
      fallback={
        <div className="py-12 text-center">
          <p className="text-gray-500">
            Loading {sectionName || 'content'}...
          </p>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
